/**
 * Types for the Carts & Transactions API
 * Note: All IDs are UUIDs (strings)
 */

type CartState = 'DRAFT' | 'PENDING' | 'APPROVED' | 'CANCELLED' | 'COMPLETED';

const TERMINAL_CART_STATES: ReadonlySet<CartState> = new Set(['COMPLETED', 'CANCELLED']);

// ==================
// Request types
// ==================

type UnitSelection = {
  id: string;
  quantity: number;
  label?: string;
};

type CustomerInfo = {
  email?: string;
  name?: string;
  firstname?: string;
  phone?: string;
};

type CreateGuestCustomerRequest = {
  firstname: string;
  name: string;
  email: string;
  phone?: string;
};

type GuestCustomerResponse = {
  id: string;
  firstname: string;
  name: string;
  email: string;
  phone: string | null;
};

type UpdateCustomerRequest = {
  firstname: string;
  name: string;
  email: string;
  phone?: string;
};

type AddToCartRequest = {
  productId: string;
  paymentProductRef: string;
  optionId: string;
  availabilityId: string;
  localDate: string;
  units: UnitSelection[];
  distributorId: string;
};

type SaveParticipantsRequest = {
  participants: Array<{
    unitId: string;
    participantIndex: number;
    fields: Array<{
      name: string;
      value: string;
    }>;
  }>;
  leadTravelerUnitId?: string;
  leadTravelerParticipantIndex?: number;
  optionFields?: Array<{
    name: string;
    value: string;
  }>;
};

type StartTransactionRequest = {
  locale: string;
  returnUrl: string;
};

// ==================
// Response types
// ==================

type CartResponse = {
  referenceNumber: number;
  distributorId: string;
  state: CartState;
  totalPrice: number;
  currency: string;
  currencyPrecision: number;
  items: Array<CartItemResponse>;
};

type CartItemResponse = {
  itemId: string;
  bookingId: string;
  productId: string;
  optionId: string;
  availabilityId: string;
  units: Array<UnitResponse>;
};

type UnitResponse = {
  unitId: string;
  sourceUnitId: string;
  description?: string;
  price: number;
  quantity: number;
  retailPrice: number;
  originalPrice?: number;
  participants: Array<UnitParticipantResponse>;
};

type UnitParticipantResponse = {
  participantId: string;
  fieldValues: Array<UnitParticipantFieldValueResponse>;
};

type UnitParticipantFieldValueResponse = {
  fieldId: string;
  fieldName: string;
  value: string;
};

type StartTransactionResponse = {
  widget: { html: string } | null;
};

type ResumeTransactionResponse = {
  status: 'RESUMABLE' | 'COMPLETED' | 'FAILED';
  widget?: { html: string } | null;
};

type SemanticType = 'FIRST_NAME' | 'LAST_NAME' | 'EMAIL_ADDRESS' | 'PHONE_NUMBER' | 'POSTAL_CODE' | 'COUNTRY' | 'OTHER';

type BookingContactField = {
  sourceId?: string | null;
  name: string;
  type: string;
  defaultValue: string;
  required: boolean;
  availableValues: Array<Record<string, string>>;
  semanticType?: SemanticType;
};

type RequiredCustomerField = {
  semanticType: SemanticType;
  name: string;
  type: string;
  required: boolean;
  defaultValue: string;
  availableValues: Array<Record<string, string>> | null;
  itemFields: Array<{
    itemId: string;
    sourceId: string;
  }>;
};

type BookingItemDetails = {
  productName: string;
  productImageUrl: string | null;
  sessionLocalDateTimeStart: string;
  bookingStatus?: 'EXPIRED' | string;
  utcExpiresAt?: string | null;
  units: Array<{
    unitId: string;
    name: string;
    requiredContactFields: Array<BookingContactField>;
  }>;
  requiredContactFields: Array<BookingContactField>;
};

type OptionFieldValueResponse = {
  fieldName: string;
  value: string;
};

type CartDetailsItemResponse = CartItemResponse & {
  bookingDetails: BookingItemDetails | null;
  bookingError: boolean;
  hasRequiredContactFields: boolean;
  leadTravelerUnitId: string | null;
  leadTravelerParticipantIndex: number | null;
  optionFieldValues: Array<OptionFieldValueResponse>;
};

type CartDetailsResponse = Omit<CartResponse, 'items'> & {
  items: Array<CartDetailsItemResponse>;
  requiredCustomerFields: Array<RequiredCustomerField>;
};

// ============================================
// Storage keys for localStorage
// ============================================

const ITEMS_COUNT_STORAGE_KEY = 'youbook_cart_items_count';

type PartialCaptureDecision = 'PARTIAL_CAPTURE' | 'CANCEL_ALL';

type PartialFailureInfo = {
  failedBookingIds: string[];
  confirmedBookingIds: string[];
  failureReasons: Record<string, string>;
};

export type {
  AddToCartRequest,
  BookingContactField,
  BookingItemDetails,
  CartDetailsItemResponse,
  CartDetailsResponse,
  CartItemResponse,
  CartResponse,
  CartState,
  CreateGuestCustomerRequest,
  CustomerInfo,
  GuestCustomerResponse,
  PartialCaptureDecision,
  PartialFailureInfo,
  RequiredCustomerField,
  ResumeTransactionResponse,
  SaveParticipantsRequest,
  SemanticType,
  StartTransactionRequest,
  StartTransactionResponse,
  UnitParticipantFieldValueResponse,
  UnitParticipantResponse,
  UnitResponse,
  UnitSelection,
  UpdateCustomerRequest,
};

export { ITEMS_COUNT_STORAGE_KEY, TERMINAL_CART_STATES };
